import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Lock, Loader2 } from 'lucide-react';

type ProfileDetail = { label: string; value: string };
type Profile = {
  name: string;
  role: string;
  memberId?: string;
  imageSrc?: string;
  details: ProfileDetail[];
};

const MemberProfile: React.FC = () => {
  const { slug = '' } = useParams<{ slug: string }>();
  const [password, setPassword] = useState('');
  const [profile, setProfile] = useState<Profile | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/verify-member', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, password }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setProfile(data.profile);
      } else {
        setError(data.error || 'Incorrect password');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
      setPassword('');
    }
  };

  // Unlocked view
  if (profile) {
    return (
      <div className="container mx-auto px-6 max-w-3xl pt-32 pb-20">
        <div className="glass-card rounded-[3rem] p-10 md:p-14 transition-theme">
          <div className="flex flex-col sm:flex-row gap-8 items-start mb-10">
            {profile.imageSrc && (
              <div className="w-32 h-32 rounded-3xl overflow-hidden flex-shrink-0 bg-[var(--accent-blue-soft)] border border-[var(--card-border)]">
                <img
                  src={profile.imageSrc}
                  alt={profile.name}
                  className="w-full h-full object-cover"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              </div>
            )}
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-[var(--text-main)] transition-theme">
                {profile.name}
              </h1>
              <p className="text-[var(--accent-blue)] font-semibold mt-1 uppercase tracking-wide transition-theme">
                {profile.role}
              </p>
              {profile.memberId && (
                <p className="text-[var(--text-muted)] text-sm font-mono mt-2 transition-theme">
                  ID: {profile.memberId}
                </p>
              )}
            </div>
          </div>

          <dl className="divide-y divide-[var(--card-border)] border-t border-[var(--card-border)]">
            {profile.details.map((d, i) => (
              <div key={i} className="flex items-center justify-between gap-4 py-4">
                <dt className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--text-muted)] transition-theme">
                  {d.label}
                </dt>
                <dd className="text-[var(--text-main)] font-semibold text-right transition-theme">
                  {d.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    );
  }

  // Locked view — password gate
  return (
    <div className="container mx-auto px-6 max-w-md pt-32 min-h-[70vh] flex flex-col justify-center">
      <div className="glass-card rounded-[2.5rem] p-10 transition-theme text-center">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-[var(--accent-blue-soft)] flex items-center justify-center text-[var(--accent-blue)] mb-6 transition-theme">
          <Lock className="w-6 h-6" />
        </div>
        <h1 className="text-2xl font-bold text-[var(--text-main)] mb-2 transition-theme">
          Private Profile
        </h1>
        <p className="text-[var(--text-muted)] text-sm mb-8 transition-theme">
          This page is password protected. Enter the password to continue.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            autoFocus
            className="w-full px-5 py-3 rounded-xl bg-[var(--bg-main)] border border-[var(--card-border)] text-[var(--text-main)] outline-none focus:border-[var(--accent-blue)] transition-all"
          />
          {error && <p className="text-sm text-red-500">{error}</p>}
          <button
            type="submit"
            disabled={loading || !password}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[var(--accent-blue)] text-white rounded-xl font-bold hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Unlock'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default MemberProfile;
